/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import BEIcon from '@molecules/BEIcon';
import { usePopover } from '@molecules/BEOffScreen';
import { BEPopover, PopoverProps } from '@molecules/BEOffScreen/Popover';
import { TabsContext, useTabsContext } from '@molecules/BETabs';
import React, {
  Children,
  cloneElement,
  forwardRef,
  useMemo,
  useState,
} from 'react';
import { BETabProps } from './BETabProps';
import { StyledTabWrap, StyledTab, StyledTabDropdown } from './StyledTab';

const BETab = forwardRef<HTMLButtonElement | HTMLAnchorElement, BETabProps>(
  (
    {
      children,
      dropdown,
      icon = null,
      id,
      isDropdownItem = false,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      panelId,
      ...rest
    },
    ref
  ) => {
    const [wrapperRef, setWrapperRef] = useState<HTMLElement>();
    const { currentTab, setCurrentTab } = useTabsContext();

    const isActive =
      currentTab === id ||
      dropdown?.props.children.some((c) => c.props.id === currentTab);

    const DropdownMenu = useMemo(() => {
      return (popoverProps: PopoverProps) => (
        <BEPopover {...popoverProps}>
          <TabsContext.Provider value={{ currentTab, setCurrentTab }}>
            <StyledTabDropdown id={panelId}>
              {Children.map(
                dropdown?.props.children,
                (child: React.ReactElement) => {
                  return cloneElement(child, {
                    ...child.props,
                    isDropdownItem: true,
                    onClick: () => {
                      setCurrentTab(child.props.id);
                      popoverProps.onOk?.();
                    },
                  });
                }
              )}
            </StyledTabDropdown>
          </TabsContext.Provider>
        </BEPopover>
      );
    }, [dropdown, panelId, currentTab, setCurrentTab]);

    const dropdownPopover = usePopover(DropdownMenu, {
      referenceElement: wrapperRef,
      width: wrapperRef?.offsetWidth,
      onOk: () => {
        dropdownPopover.close();
      },
    });

    const _handleClick = (
      e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
      if (dropdown) {
        if (isOpen) {
          dropdownPopover.close();
        } else {
          dropdownPopover.open();
        }
      } else {
        setCurrentTab(id);
      }

      if (typeof handleClick === 'function') {
        handleClick(e);
      }
    };

    const isOpen = dropdownPopover.isOpened();

    return (
      <StyledTabWrap
        id={id}
        isActive={isActive}
        isDropdownItem={isDropdownItem}
        ref={setWrapperRef as any}
      >
        <StyledTab
          {...rest}
          aria-controls={panelId}
          aria-disabled={rest.disabled}
          aria-selected={isActive}
          id={id}
          isActive={dropdown ? isOpen : isActive}
          onClick={_handleClick}
          onKeyDown={dropdown && isOpen ? undefined : handleKeyDown}
          ref={ref as any}
          tabIndex={rest.disabled ? -1 : rest.tabIndex}
        >
          <div>{icon ? cloneElement(icon, { size: 'sm' }) : null}</div>
          {children}
          {dropdown ? (
            <BEIcon name={isOpen ? 'angle-up' : 'angle-down'} size="xs" />
          ) : null}
        </StyledTab>
      </StyledTabWrap>
    );
  }
);

export default BETab;
